/**
 * The banner that asks a magic-link session to set up a personal sign-in.
 *
 * Alan's choice was a transition WITH a prompt (spec 2026-09-22): the brand
 * magic link keeps working, and the portal asks. So this is a banner and not a
 * modal - it sits above the content, blocks nothing and loses no typing.
 *
 * Its button is the banner's primary and NOT the screen's: on Home the loud
 * orange belongs to "Request ads" and to "New brief" in the rail, and a third
 * orange button at the top of the page took the eye off both. The screenshot is
 * what showed it. So the banner asks in glass - first on the page, inside its
 * own frame, 48px tall - and the page keeps one dominant action.
 *
 * Waving it away lasts the visit, not the device. That is deliberate: next
 * visit asks again.
 *
 * The decision itself is not here - `passwordNudgeState` owns it, and this
 * component only renders what it returns. A brand that never loaded, a server
 * without the field, a storage that throws: all handled there, and all of it
 * lands on "hidden" or "show it anyway", never on an exception.
 *
 * @module web/components/PasswordNudge
 */
import { useState } from "react";
import { useBrand } from "../state/BrandContext.tsx";
import {
  browserVisitStore,
  passwordNudgeState,
  readDismissed,
  rememberDismissed,
} from "../screens/app/passwordNudge.ts";

export function PasswordNudge() {
  const { brand } = useBrand();
  const [store] = useState(browserVisitStore);
  const [dismissed, setDismissed] = useState(() => readDismissed(store));
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (passwordNudgeState(brand, dismissed) === "hidden") return null;

  return (
    /* `status`, not `alert`: it is worth reading, not worth interrupting. */
    <div className="pw-nudge" role="status">
      <p className={`pw-nudge-text${error ? " err" : ""}`}>
        {error ?? (sent
          ? "Check your inbox - the link is on its way."
          : "You're signed in from an email link. Set up a personal sign-in and you're in from any device.")}
      </p>
      <div className="pw-nudge-actions">
        {!sent && (
          <button type="button" className="btn pw-nudge-cta" onClick={() => void request()} disabled={busy}>
            {busy ? "Sending…" : "Set up access"}
          </button>
        )}
        <button type="button" className="btn btn-ghost pw-nudge-dismiss" onClick={dismiss}>
          Not now
        </button>
      </div>
    </div>
  );

  function dismiss(): void {
    rememberDismissed(store);
    // The component's own state is what hides it this render; the store is only
    // what makes the decision stick across the rest of the visit. A storage
    // that refuses to remember still gets the banner out of the way right now.
    setDismissed(true);
  }

  async function request(): Promise<void> {
    if (busy || !brand?.email) return;
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/auth/password/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: brand.email }),
      });
      if (res.status === 429) {
        setError("Too many attempts. Please wait a few minutes and try again.");
        return;
      }
      if (!res.ok) {
        setError("Something went wrong. Please try again or email hey@aditor.ai.");
        return;
      }
      setSent(true);
    } catch {
      setError("Network error. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }
}
